import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { fetchApprovalHistory, submitForApproval } from '@/approvals/api'
import type { ApprovalRequest, ApprovalStep } from '@/approvals/types'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import { formatDate, formatMoney, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { cardHref } from '@/lists/cellValue'
import { HistoryPanel } from './HistoryPanel'
import { acceptQuote, fetchQuote, lineNet, rejectQuote, sendQuote, type Quote } from './quoteApi'
import {
  Facts,
  Panel,
  RecordHeader,
  RecordTabs,
  StageBar,
  StateView,
  type Fact,
  type HeaderAction,
  type MenuAction,
  type StageStep,
} from './record'
import { listPath } from './recordHelpers'
import { FailureAlert, RecordFailure, RecordSkeleton, StatusBadge, copyLinkItem, failureText, labelOf } from './recordKit'
import { EditActions } from './LeadRecordView'
import type { RecordViewProps } from './types'

/**
 * Карточка коммерческого предложения (Quote.dc.html).
 *
 * Действия в шапке по статусу: черновик отправляют клиенту (пустой — нельзя, и причина
 * написана рядом с кнопкой, а не спрятана в неактивности), отправленное отмечают принятым
 * или отклонённым. Порог скидки проверяет сервер при отправке; черновик над порогом можно
 * отправить на согласование, и цепочка шагов (`.chain`) показывает, кто уже решил и кого
 * ждём, — из истории согласований `quote`.
 *
 * ## Чего здесь нет, хотя макет это рисует
 *
 * - **Версии КП, «Новая версия», «Дублировать», «Отозвать КП», PDF** — эндпоинтов нет.
 * - **Получатель КП, «открыто клиентом», число просмотров** — КП их не хранит.
 * - **Условия (оплата, доставка, срок поставки)** — полей у КП нет.
 * - **Добавление позиций с экрана**: `POST /quotes/{id}/lines` есть, но выбор цены и ставки
 *   НДС на карточке — отдельная форма; позиции переносятся из сделки при создании КП.
 * - **Остаток на складе у строки** — КП его не отдаёт.
 */
export function QuoteRecordView({ recordId, record, context, form, notices, edit }: RecordViewProps) {
  const { t, user, locale } = useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState('lines')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const quote = useQuery({ queryKey: ['quote', recordId], queryFn: ({ signal }) => fetchQuote(recordId, signal), retry: false })
  const approvals = useQuery({
    queryKey: ['approval-history', 'quote', recordId],
    queryFn: ({ signal }) => fetchApprovalHistory('quote', recordId, signal),
    retry: false,
  })
  const productIds = [...new Set((quote.data?.lines ?? []).map((line) => line.productId).filter((id): id is string => Boolean(id)))]
  const products = useQuery({
    queryKey: ['quote-products', productIds],
    enabled: productIds.length > 0 && can(user, 'sales.product', 'read'),
    queryFn: ({ signal }) =>
      fetchPage(
        'sales.product',
        { columns: ['name', 'sku'], filters: [{ column: 'id', operator: 'in', value: productIds }], sort: [], pageSize: 100 },
        null,
        signal,
      ),
    retry: false,
  })

  const mayUpdate = can(user, 'sales.quote', 'update')
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['quote', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['approval-history', 'quote', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['record', 'sales.quote', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'sales.quote'] })
  }
  const done = (text: string | null) => () => {
    setFailure(null)
    setNotice(text)
    refresh()
  }
  const fail = (error: unknown) => {
    setNotice(null)
    setFailure(failureText(error, t('app.error')))
  }
  const send = useMutation({ mutationFn: () => sendQuote(recordId), onSuccess: done(t('record.quote.sent')), onError: fail })
  const accept = useMutation({ mutationFn: () => acceptQuote(recordId), onSuccess: done(t('record.quote.accepted')), onError: fail })
  const reject = useMutation({ mutationFn: () => rejectQuote(recordId), onSuccess: done(null), onError: fail })
  const submit = useMutation({
    mutationFn: () => submitForApproval('quote', recordId),
    onSuccess: (result) => done(result.required ? t('record.quote.approval.submitted') : t('record.quote.approval.notRequired'))(),
    onError: fail,
  })

  if (quote.isLoading) return <RecordSkeleton />
  if (!quote.data) {
    return <RecordFailure error={quote.error} onRetry={() => void quote.refetch()} listPath={listPath('sales.quote', '/quotes')} />
  }
  const current = quote.data
  const busy = send.isPending || accept.isPending || reject.isPending || submit.isPending
  const latest = latestRequest(approvals.data ?? [])
  const pendingApproval = latest?.status === 'pending'
  const empty = current.lines.length === 0
  const money = (value: number) => formatMoney(value, current.currency, locale)

  // ------------------------------------------------------------------ шапка

  const counterpartyName = labelOf(record.values, 'counterparty_id')
  const counterpartyHref = current.counterpartyId ? cardHref(user, 'sales.counterparty', current.counterpartyId) : null
  const dealNumber = labelOf(record.values, 'deal_id')
  const dealHref = current.dealId ? cardHref(user, 'sales.deal', current.dealId) : null
  const meta = [
    <StatusBadge key="status" entity="sales.quote" value={current.status} />,
    pendingApproval ? (
      <span key="approval" className="badge b-info">
        <Icon name="clock" />
        {t('record.quote.needsApproval')}
      </span>
    ) : null,
    counterpartyName ? (
      <span key="client" className="row" style={{ gap: 6 }}>
        <Icon name="building" size="sm" />
        {counterpartyHref ? <Link to={counterpartyHref}>{counterpartyName}</Link> : counterpartyName}
      </span>
    ) : null,
    current.dealId ? (
      <span key="deal">
        {t('record.quote.deal')}{' '}
        {dealHref ? <Link to={dealHref} className="mono">{dealNumber ?? t('record.quote.dealLink')}</Link> : <span className="mono">{dealNumber}</span>}
      </span>
    ) : null,
    labelOf(record.values, 'organization_id') ? t('record.quote.from', { org: labelOf(record.values, 'organization_id') ?? '' }) : null,
  ]

  let primary: HeaderAction | null = null
  const secondary: HeaderAction[] = []
  let why: string | null = null
  if (mayUpdate && current.status === 'draft') {
    if (empty) why = t('record.quote.why.empty')
    else if (pendingApproval) why = t('record.quote.why.approval')
    primary = {
      id: 'send',
      label: t('record.quote.send'),
      icon: 'send',
      onClick: () => send.mutate(),
      disabled: busy || why != null,
    }
    if (!empty && !pendingApproval) {
      secondary.push({ id: 'approval', label: t('record.quote.submitApproval'), icon: 'check', onClick: () => submit.mutate(), disabled: busy })
    }
  }
  if (mayUpdate && current.status === 'sent') {
    secondary.push({ id: 'reject', label: t('record.quote.markRejected'), onClick: () => reject.mutate(), disabled: busy })
    primary = { id: 'accept', label: t('record.quote.markAccepted'), icon: 'check', onClick: () => accept.mutate(), disabled: busy }
  }
  const menu: MenuAction[] = [copyLinkItem(t, setFailure)]

  // ------------------------------------------------------------------ этапы и факты

  const sent = current.status !== 'draft'
  const answered = current.status === 'accepted' || current.status === 'rejected' || current.status === 'expired'
  const steps: StageStep[] = [
    {
      id: 'draft',
      label: t('value.sales.quote.status.draft'),
      state: sent ? 'done' : 'current',
      meta: !sent ? (empty ? t('record.quote.stage.noLines') : pendingApproval ? t('record.quote.stage.onApproval') : undefined) : undefined,
    },
    {
      id: 'sent',
      label: t('value.sales.quote.status.sent'),
      state: answered ? 'done' : sent ? 'current' : 'future',
      meta: current.sentAt ? formatDate(current.sentAt.slice(0, 10), locale) : undefined,
    },
    {
      id: 'answer',
      label: answered ? t(`value.sales.quote.status.${current.status}`) : t('record.quote.stage.answer'),
      state: answered ? 'current' : 'future',
      meta: current.validUntil ? t('record.quote.stage.until', { date: formatDate(current.validUntil, locale) }) : undefined,
    },
  ]

  const discountShare = current.subtotal > 0 ? (current.discountTotal / current.subtotal) * 100 : 0
  const facts: Fact[] = [
    {
      id: 'total',
      label: t('record.quote.fact.total'),
      value: money(current.total),
      sub: t('record.quote.fact.totalSub', { net: money(current.subtotal - current.discountTotal), vat: money(current.vatTotal) }),
    },
    {
      id: 'discount',
      label: t('record.quote.fact.discount'),
      value: current.discountTotal > 0 ? money(current.discountTotal) : t('record.quote.noDiscount'),
      sub: current.discountTotal > 0 ? t('record.quote.fact.discountSub', { share: formatNumber(Math.round(discountShare * 100) / 100, locale) }) : undefined,
    },
    {
      id: 'valid',
      label: t('record.quote.fact.validUntil'),
      value: current.validUntil ? formatDate(current.validUntil, locale) : t('record.notSet'),
    },
    {
      id: 'sent',
      label: t('record.quote.fact.sent'),
      value: current.sentAt ? formatDate(current.sentAt.slice(0, 10), locale) : t('record.quote.notSent'),
    },
  ]

  const productOf = (id: string | null) => (products.data?.rows ?? []).find((row) => row.id === id)

  const lines = (
    <Panel
      title={t('record.tab.positions')}
      count={current.lines.length}
      flush
      actions={
        sent ? <span className="flag flag-muted">{t('record.quote.locked')}</span> : pendingApproval ? <span className="flag flag-muted">{t('record.quote.lockedApproval')}</span> : undefined
      }
    >
      {empty ? (
        <StateView compact kind="empty" title={t('record.quote.lines.empty')} description={t('record.quote.lines.emptyText')} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl" aria-label={t('record.tab.positions')}>
            <thead>
              <tr>
                <th>{t('card.deal.line.product')}</th>
                <th className="num">{t('card.deal.line.quantity')}</th>
                <th className="num">{t('record.quote.col.price')}</th>
                <th className="num">{t('record.quote.col.discount')}</th>
                <th className="num">{t('record.quote.col.net')}</th>
                <th className="num">{t('record.quote.col.vat')}</th>
                <th className="num">{t('record.quote.col.total')}</th>
              </tr>
            </thead>
            <tbody>
              {current.lines.map((line) => {
                const product = productOf(line.productId)
                const net = lineNet(line)
                return (
                  <tr key={line.id}>
                    <td className="wrap">
                      <span className="stack" style={{ gap: 0 }}>
                        {product?.name ? (
                          <Link to={`${listPath('sales.product', '/products')}/${line.productId}`}>{String(product.name)}</Link>
                        ) : (
                          <span className="muted">{t('record.quote.productUnknown')}</span>
                        )}
                        {product?.sku ? <span className="mono muted t-xs">{String(product.sku)}</span> : null}
                      </span>
                    </td>
                    <td className="num">{formatNumber(line.quantity, locale)}</td>
                    <td className="num">{money(line.unitPrice)}</td>
                    <td className="num">
                      {line.discountPercent > 0 ? (
                        <span className={pendingApproval ? 'flag flag-warning' : undefined}>{formatNumber(line.discountPercent, locale)} %</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num">{money(net)}</td>
                    <td className="num">
                      <span className="stack" style={{ gap: 0, alignItems: 'flex-end' }}>
                        <span>{money(Math.round((line.lineTotal - net) * 100) / 100)}</span>
                        <span className="t-xs muted">{formatNumber(line.vatRate, locale)} %</span>
                      </span>
                    </td>
                    <td className="num strong">{money(line.lineTotal)}</td>
                  </tr>
                )
              })}
              <tr className="total">
                <td>{t('card.deal.lines.total')}</td>
                <td />
                <td />
                <td className="num">{current.discountTotal > 0 ? `−${money(current.discountTotal)}` : ''}</td>
                <td className="num">{money(current.subtotal - current.discountTotal)}</td>
                <td className="num">{money(current.vatTotal)}</td>
                <td className="num">{money(current.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  )

  const approvalPanel = (
    <Panel
      title={t('record.quote.approval')}
      actions={latest ? <ApprovalBadge status={latest.status} /> : <span className="badge">{t('record.quote.approval.none')}</span>}
    >
      {approvals.isLoading ? (
        <p className="muted">{t('app.loading')}</p>
      ) : approvals.isError ? (
        <StateView
          compact
          kind={approvals.error instanceof ApiError && approvals.error.isForbidden ? 'forbidden' : 'error'}
          title={t('record.quote.approval.error')}
        />
      ) : latest ? (
        <ApprovalChain request={latest} />
      ) : (
        <p className="secondary">{t('record.quote.approval.noneText')}</p>
      )}
    </Panel>
  )

  return (
    <div className="rec-page">
      <RecordHeader
        crumbs={[{ label: t('nav.quotes'), to: listPath('sales.quote', '/quotes') }, { label: current.number, mono: true }]}
        title={
          <>
            {t('record.quote.title')} <span className="rec-title-mono">{current.number}</span>
          </>
        }
        meta={meta}
        primary={primary}
        secondary={secondary}
        menu={menu}
        menuLabel={t('record.more')}
      />
      {why && (
        <p className="t-sm muted" role="status" style={{ margin: 0, textAlign: 'right' }}>
          {why}
        </p>
      )}
      <FailureAlert text={failure} />
      {notice && (
        <div className="alert alert-success" role="status">
          <Icon name="check" />
          <span>{notice}</span>
        </div>
      )}
      {notices}
      {pendingApproval && (
        <div className="alert alert-warning" role="status">
          <Icon name="alert" />
          <span>{t('record.quote.pendingAlert')}</span>
        </div>
      )}

      <StageBar steps={steps} ariaLabel={t('record.quote.stages')} />
      <Facts items={facts} ariaLabel={t('record.facts')} />

      <RecordTabs
        tabs={[
          { id: 'lines', label: t('record.tab.positions'), count: current.lines.length },
          { id: 'details', label: t('record.quote.tab.details') },
          { id: 'history', label: t('record.tab.history') },
        ]}
        active={tab}
        onChange={setTab}
        ariaLabel={t('record.quote.sections')}
      >
        {tab === 'lines' && (
          <div className="layout-2-wide" style={{ paddingTop: 14 }}>
            <div className="rec-col">{lines}</div>
            <div className="rec-col">{approvalPanel}</div>
          </div>
        )}
        {tab === 'details' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.quote.details')} actions={<EditActions edit={edit} />}>
              {form}
            </Panel>
          </div>
        )}
        {tab === 'history' && (
          <div className="rec-col" style={{ paddingTop: 14 }}>
            <Panel title={t('record.history.changes')}>
              <HistoryPanel entity={context.entity} recordId={recordId} />
            </Panel>
          </div>
        )}
      </RecordTabs>
    </div>
  )
}

/** Последний запрос — тот, что решает сейчас; прежние остаются в журнале. */
function latestRequest(requests: ApprovalRequest[]): ApprovalRequest | null {
  return [...requests].sort((a, b) => Date.parse(b.requestedAt) - Date.parse(a.requestedAt))[0] ?? null
}

function ApprovalBadge({ status }: { status: ApprovalRequest['status'] }) {
  const { t } = useApp()
  const tone = status === 'approved' ? 'b-success' : status === 'rejected' ? 'b-danger' : status === 'pending' ? 'b-info' : ''
  return (
    <span className={`badge ${tone}`.trim()}>
      <Icon name={status === 'approved' ? 'check' : status === 'rejected' ? 'close' : 'clock'} />
      {t(`record.quote.approval.status.${status}`)}
    </span>
  )
}

/** Цепочка шагов: решённые — с датой и комментарием, текущий — «ждёт», остальные — впереди. */
export function ApprovalChain({ request }: { request: ApprovalRequest }) {
  const { t, locale } = useApp()
  const steps = [...request.steps].sort((a, b) => a.stepOrder - b.stepOrder)
  return (
    <div className="chain" aria-label={t('record.quote.approval')}>
      {steps.map((step) => {
        const state = stepState(step, request)
        return (
          <div key={step.id} className={`chain-step ${state.cls}`.trim()}>
            <span className="chain-dot">
              {step.status === 'approved' ? <Icon name="check" /> : step.status === 'rejected' ? <Icon name="close" /> : step.stepOrder}
            </span>
            <div className="stack" style={{ gap: 0 }}>
              <span className={state.current || step.status !== 'pending' ? 'strong' : 'secondary'}>
                {step.approverName ?? t('record.quote.approval.byRole')}
              </span>
              <span className="t-sm secondary">
                {step.decidedAt
                  ? [new Date(step.decidedAt).toLocaleString(locale === 'en' ? 'en-GB' : 'ru-RU'), step.comment ? `«${step.comment}»` : null]
                      .filter(Boolean)
                      .join(' · ')
                  : state.current
                    ? t('record.quote.approval.waiting')
                    : t('record.quote.approval.ahead')}
              </span>
            </div>
            <span className={`badge ${state.badge}`.trim()}>{t(`record.quote.approval.step.${state.key}`)}</span>
          </div>
        )
      })}
    </div>
  )
}

function stepState(step: ApprovalStep, request: ApprovalRequest) {
  if (step.status === 'approved') return { cls: 'is-done', badge: 'b-success', key: 'approved', current: false }
  if (step.status === 'rejected') return { cls: 'is-rejected', badge: 'b-danger', key: 'rejected', current: false }
  if (step.status === 'skipped') return { cls: 'is-skipped', badge: '', key: 'skipped', current: false }
  const current = request.status === 'pending' && step.stepOrder === request.currentStep
  return current
    ? { cls: 'is-current', badge: 'b-info', key: 'waiting', current: true }
    : { cls: '', badge: '', key: 'ahead', current: false }
}

export type { Quote }
